import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { supabase } from '../supabaseClient';

function NotificationSystem() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [operateurId, setOperateurId] = useState(null);

  useEffect(() => {
    // Récupérer l'opérateur connecté
    const operateurLocal = localStorage.getItem('operateur');
    if (operateurLocal) {
      const op = JSON.parse(operateurLocal);
      setOperateurId(op.id);
    }
  }, []);

  useEffect(() => {
    if (!operateurId) return;

    // Charger les notifications actives au démarrage
    chargerNotificationsActives();

    // S'abonner aux nouvelles notifications via Realtime
    // Écouter TOUTES les notifications (globales + ciblées)
    const channel = supabase
      .channel('notifications-channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          // Filtrer côté client : notifications globales OU ciblées pour cet opérateur
          if (!payload.new.operateur_id || payload.new.operateur_id === operateurId) {
            afficherNotification(payload.new);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [operateurId]);

  const chargerNotificationsActives = async () => {
    if (!operateurId) return;

    // Charger les notifications actives : globales (operateur_id NULL) OU ciblées pour cet opérateur
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('active', true)
      .or(`operateur_id.is.null,operateur_id.eq.${operateurId}`)
      .order('date_creation', { ascending: false });

    if (error) {
      console.error('Erreur chargement notifications:', error);
      return;
    }

    setNotifications(data || []);

    // Afficher les notifications non vues
    (data || []).forEach(notif => {
      // TODO: Vérifier si l'opérateur connecté a déjà vu cette notification
      afficherNotification(notif);
    });
  };

  const afficherNotification = (notification) => {
    const { type, priorite, titre, message, id, carte_id, metadata } = notification;

    // Configuration selon la priorité
    const config = {
      critique: {
        autoClose: false, // Ne se ferme pas automatiquement
        closeOnClick: false,
        draggable: false,
        closeButton: false, // Pas de bouton X
        style: {
          backgroundColor: '#fee2e2',
          border: '2px solid #ef4444',
          color: '#7f1d1d'
        },
        progressStyle: {
          background: '#ef4444'
        }
      },
      moyenne: {
        autoClose: false, // Ne se ferme pas automatiquement (comme critique)
        closeOnClick: false,
        draggable: false,
        closeButton: false, // Pas de bouton X (comme critique)
        style: {
          backgroundColor: '#dbeafe',
          border: '2px solid #3b82f6',
          color: '#1e3a8a'
        },
        progressStyle: {
          background: '#3b82f6'
        }
      },
      basse: {
        autoClose: 5000, // 5 secondes
        closeOnClick: true,
        style: {
          backgroundColor: '#dbeafe',
          border: '2px solid #3b82f6',
          color: '#1e3a8a'
        },
        progressStyle: {
          background: '#3b82f6'
        }
      }
    };

    const toastConfig = config[priorite] || config.basse;

    // Composant personnalisé pour les notifications critiques
    const NotificationCritique = () => (
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>🚨</span>
          <div>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              marginBottom: '4px'
            }}>
              {titre}
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
              {message}
            </div>
          </div>
        </div>
        {type === 'nouveau_commentaire' ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                marquerCommeVue(id);
                // Si on est déjà sur Planning, émettre l'événement directement
                if (window.location.pathname === '/admin_4Sd6c3P7/planning') {
                  window.dispatchEvent(new CustomEvent('ouvrir-commentaires', { 
                    detail: { carteId: carte_id } 
                  }));
                } else {
                  // Sinon, stocker pour l'ouvrir après navigation
                  localStorage.setItem('ouvrir_commentaire_carte', carte_id);
                  navigate('/admin_4Sd6c3P7/planning');
                }
              }}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
            >
              Voir le commentaire
            </button>
            <button
              onClick={() => marquerCommeVue(id)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b7280'}
            >
              OK
            </button>
          </div>
        ) : (
          <button
            onClick={() => marquerCommeVue(id)}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
          >
            J'ai compris
          </button>
        )}
      </div>
    );

    // Composant standard pour les autres priorités (Info)
    const NotificationStandard = () => (
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>{getEmoji(priorite, type)}</span>
          <div>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              marginBottom: '4px'
            }}>
              {titre}
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
              {message}
            </div>
          </div>
        </div>
        {type === 'nouveau_commentaire' ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                marquerCommeVue(id);
                // Si on est déjà sur Planning, émettre l'événement directement
                if (window.location.pathname === '/admin_4Sd6c3P7/planning') {
                  window.dispatchEvent(new CustomEvent('ouvrir-commentaires', { 
                    detail: { carteId: carte_id } 
                  }));
                } else {
                  // Sinon, stocker pour l'ouvrir après navigation
                  localStorage.setItem('ouvrir_commentaire_carte', carte_id);
                  navigate('/admin_4Sd6c3P7/planning');
                }
              }}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            >
              Voir le commentaire
            </button>
            <button
              onClick={() => marquerCommeVue(id)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b7280'}
            >
              OK
            </button>
          </div>
        ) : (
          <button
            onClick={() => marquerCommeVue(id)}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            J'ai compris
          </button>
        )}
      </div>
    );

    // Afficher selon la priorité
    // Critique et Moyenne (Info) utilisent le même affichage avec bouton
    if (priorite === 'critique' || priorite === 'moyenne') {
      const Component = priorite === 'critique' ? NotificationCritique : NotificationStandard;
      toast(<Component />, {
        ...toastConfig,
        toastId: id, // Éviter les doublons
        position: 'bottom-right'
      });
      
      // Jouer le son approprié
      console.log('🎵 Vérification son - priorité:', priorite, 'type:', type);
      if (priorite === 'critique') {
        // Toutes les notifications critiques = alerte.mp3
        console.log('🚨 Notification CRITIQUE détectée - lecture alerte');
        jouerSonOld('attention');
      } else if (type === 'nouveau_commentaire' && metadata) {
        // Notifications Info commentaires = Notification.mp3
        console.log('ℹ️ Commentaire INFO détecté - lecture notification');
        jouerSonOld('info');
      } else {
        console.log('⚠️ Pas de son joué (priorité moyenne non-commentaire)');
      }
    } else {
      // Autres priorités (basse) - comportement standard sans bouton
      toast(<NotificationStandard />, {
        ...toastConfig,
        toastId: id,
        position: 'bottom-right',
        onClose: () => {
          marquerCommeVue(id);
        }
      });
    }
  };

  const marquerCommeVue = async (notificationId) => {
    try {
      // Fermer le toast
      toast.dismiss(notificationId);

      // Utiliser la fonction SQL pour désactiver (évite problèmes RLS)
      const { error } = await supabase
        .rpc('desactiver_notification', { 
          p_notification_id: notificationId 
        });

      if (error) {
        console.error('Erreur marquage notification:', error);
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const getEmoji = (priorite, type) => {
    if (type === 'changement_semaine') return '📅';
    if (type === 'carte_retard') return '⚠️';
    if (type === 'report_automatique') return '🔄';
    if (type === 'terminaison_anticipee') return '🎉';
    
    if (priorite === 'critique') return '🚨';
    if (priorite === 'moyenne') return '⚠️';
    return 'ℹ️';
  };

  const jouerSonOld = (typeCommentaire) => {
    // Jouer le son approprié selon le type de commentaire
    console.log('🔊 Tentative de jouer son pour type:', typeCommentaire);
    try {
      let fichierSon;
      if (typeCommentaire === 'attention') {
        fichierSon = '/sounds/Alerte.mp3'; // Son pour Attention (A majuscule)
      } else {
        fichierSon = '/sounds/Notification.mp3'; // Son pour Info
      }
      
      console.log('🔊 Fichier son sélectionné:', fichierSon);
      const audio = new Audio(fichierSon);
      audio.volume = 1.0; // Volume à 100% (du volume système)
      audio.play()
        .then(() => console.log('✅ Son joué avec succès'))
        .catch((err) => {
          console.error('❌ Erreur autoplay:', err);
        });
    } catch (err) {
      console.error('❌ Erreur création audio:', err);
    }
  };

  return (
    <ToastContainer
      position="bottom-right"
      newestOnTop
      closeButton={true}
      rtl={false}
      pauseOnFocusLoss
      draggable={false}
      pauseOnHover
      theme="light"
      limit={3} // Maximum 3 notifications visibles en même temps
      style={{
        zIndex: 9999
      }}
    />
  );
}

export default NotificationSystem;
